import React, { useState } from "react";
import Layout from "../../components/web/Layout";

import { AlertCircle, Trash2, Mail, Shield, CheckCircle } from "lucide-react";

export default function DeleteAccount() {
  const [unionCode, setUnionCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState("");

  const handleVerify = async () => {
    if (!unionCode.trim()) {
      setError("Please enter your Union Code");
      return;
    }

    setLoading(true);
    setError("");
    setResponse(null);

    try {
      const result = await fetch(
        "https://actadcd.in/backend/api/delete-account",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            union_code: unionCode,
          }),
        }
      );

      const data = await result.json();

      if (data.response) {
        setResponse(data);
      } else {
        setError(data.msg || "Something went wrong");
      }
    } catch (err) {
      setError("Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="bg-gray-50 py-12 md:py-20 flex justify-center">
        <div className="w-full max-w-md">
          {/* Warning Banner */}
          <div className="bg-red-500 text-white px-4 py-3 rounded-t-2xl flex items-center gap-3 shadow-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">Account Deletion Process</p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-b-2xl shadow-2xl overflow-hidden">
            <div className="p-8">
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="bg-red-100 p-4 rounded-full">
                  <Trash2 className="w-12 h-12 text-red-600" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
                Delete Account
              </h1>
              <p className="text-center text-gray-600 mb-8">
                Enter your Union Code to proceed with account deletion
              </p>

              {/* Success Message */}
              {response && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 animate-fadeIn">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-800 font-medium mb-1">Success!</p>
                      <p className="text-green-700 text-sm">{response.msg}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 animate-fadeIn">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Input Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Union Code
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={unionCode}
                    onChange={(e) => setUnionCode(e.target.value)}
                    placeholder="Enter your union code"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors"
                    disabled={loading}
                  />
                  <Shield className="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Verify Button */}
              <button
                onClick={handleVerify}
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="w-5 h-5" />
                    <span>Verify & Send Code</span>
                  </div>
                )}
              </button>

              {/* Info Box */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-800 font-medium text-sm mb-1">
                      Verification Email
                    </p>
                    <p className="text-blue-700 text-xs">
                      A verification code will be sent to your registered email address
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
              <p className="text-center text-xs text-gray-500">
                This action cannot be undone. Please ensure you want to delete your
                account.
              </p>
            </div>
          </div>
        </div>

        {/* Fade Animation */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        `}</style>
      </div>
    </Layout>
  );
}
